import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

const ToastContext = createContext();

// Animations
const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const slideOut = keyframes`
  from { transform: translateX(0); opacity: 1; }
  to { transform: translateX(100%); opacity: 0; }
`;

// Styles
const ToastContainer = styled.div`
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ToastItem = styled.div`
  background: #1a1a1a;
  color: #fff;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 300px;
  max-width: 400px;
  border-left: 4px solid ${props =>
        props.$type === 'success' ? '#10b981' :
            props.$type === 'error' ? '#ef4444' : '#3b82f6'};
  animation: ${props => props.$closing ? slideOut : slideIn} 0.3s ease-out forwards;
  
  font-family: 'Pretendard', sans-serif;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  margin-left: auto;
  padding: 4px;
  display: flex;
  align-items: center;
  
  &:hover { color: #fff; }
`;

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.map(t => t.id === id ? { ...t, closing: true } : t));

        // Wait for animation
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 300);
    }, []);

    const addToast = useCallback((message, type = 'info') => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type, closing: false }]);

        // Auto remove after 3s
        setTimeout(() => {
            removeToast(id);
        }, 3000);
    }, [removeToast]);

    const contextValue = React.useMemo(() => ({ showToast: addToast }), [addToast]);

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
            <ToastContainer>
                {toasts.map(toast => (
                    <ToastItem key={toast.id} $type={toast.type} $closing={toast.closing}>
                        {toast.type === 'success' && <CheckCircle size={20} color="#10b981" />}
                        {toast.type === 'error' && <AlertCircle size={20} color="#ef4444" />}
                        {toast.type === 'info' && <Info size={20} color="#3b82f6" />}
                        <div>{toast.message}</div>
                        <CloseButton onClick={() => removeToast(toast.id)}>
                            <X size={16} />
                        </CloseButton>
                    </ToastItem>
                ))}
            </ToastContainer>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};
